import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { CartContext } from '@/context/CartContext';
import { usePhoneValidation } from '@/hooks/usePhoneValidation';

export default function CartDrawer() {
  const {
    items,
    isDrawerOpen,
    closeDrawer,
    removeItem,
    updateQuantity,
    subtotal,
    itemCount,
  } = useContext(CartContext);

  const { phone, error: phoneError, touched: phoneTouched, isValid: phoneValid, handlePhoneChange, handleBlur: handlePhoneBlur, persistPhone, displayPhone } = usePhoneValidation();

  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [nameError, setNameError] = useState('');
  const [cityError, setCityError] = useState('');

  const WhatsAppNumber = '919686543847';

  const buildWhatsAppMessage = () => {
    const lineItems = items
      .map(
        (item) =>
          `${item.name}${item.variant ? ` (${item.variant})` : ''} x${item.quantity} \u2013 \u20B9${item.price * item.quantity}`
      )
      .join('\n');

    return `Hi Joviaa! I'd like to order:\n\n${lineItems}\n\nSubtotal: \u20B9${subtotal}\nName: ${name || '(not provided)'}\nPhone: ${displayPhone || '(not provided)'}\nDelivery City/Pincode: ${city || '(not provided)'}\n\nPlease confirm availability and final pricing.`;
  };

  const handleCheckout = () => {
    // Validate required fields
    let hasError = false;

    if (!name.trim()) {
      setNameError('Please enter your name.');
      hasError = true;
    }

    if (!phone || !phoneValid) {
      // Trigger phone validation
      handlePhoneBlur();
      hasError = true;
    }

    if (!city.trim()) {
      setCityError('Please enter your city or pincode for delivery.');
      hasError = true;
    }

    if (hasError) return;

    // Persist phone for next time
    persistPhone();

    const message = buildWhatsAppMessage();
    const url = `https://wa.me/${WhatsAppNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setToastMessage('Opening WhatsApp — please send the message to complete your order.');
    setTimeout(() => setToastMessage(''), 4000);
  };

  const handleRemove = (productId: string) => {
    removeItem(productId);
  };

  return (
    <>
      {/* Overlay */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-foreground-950/40 z-[60] transition-opacity"
          onClick={closeDrawer}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-background-50 z-[70] transform transition-transform duration-300 ease-out flex flex-col ${
          isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <h2 className="font-heading text-lg font-semibold text-foreground-800">
            Your Bag ({itemCount} {itemCount === 1 ? 'item' : 'items'})
          </h2>
          <button
            onClick={closeDrawer}
            className="text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-close-line text-xl"></i>
            </div>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 rounded-full bg-secondary-200 flex items-center justify-center mb-4">
                <i className="ri-shopping-bag-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600 text-sm mb-1">
                Your bag is feeling light.
              </p>
              <p className="text-foreground-500 text-sm mb-5">
                Let&apos;s fix that.
              </p>
              <button
                onClick={closeDrawer}
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-md font-label font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {items.map((item) => (
                <div
                  key={item.productId + (item.variant || '')}
                  className="flex gap-3 pb-4 border-b border-background-200"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-secondary-200">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-heading font-semibold text-foreground-800 truncate">
                      {item.name}
                    </p>
                    {item.variant && (
                      <p className="text-xs text-foreground-500 mt-0.5">
                        {item.variant}
                      </p>
                    )}
                    <p className="text-sm font-heading font-bold text-primary-500 mt-1">
                      ₹{item.price * item.quantity}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-background-300 rounded-md">
                        <button
                          onClick={() =>
                            updateQuantity(item.productId, item.quantity - 1)
                          }
                          className="w-7 h-7 flex items-center justify-center text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                        >
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <span className="w-8 text-center text-sm font-label font-medium text-foreground-800">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateQuantity(item.productId, item.quantity + 1)
                          }
                          className="w-7 h-7 flex items-center justify-center text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                      <button
                        onClick={() => handleRemove(item.productId)}
                        className="text-foreground-400 hover:text-accent-700 transition-colors cursor-pointer ml-auto"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-delete-bin-line"></i>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-background-200 px-5 py-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-label font-medium text-foreground-600">
                Subtotal
              </span>
              <span className="text-lg font-heading font-bold text-primary-500">
                ₹{subtotal}
              </span>
            </div>
            <p className="text-xs text-foreground-500 italic mb-3">
              No online payment required — we&apos;ll confirm your order details
              and pricing over WhatsApp.
            </p>

            {!showForm ? (
              <button
                onClick={() => setShowForm(true)}
                className="w-full py-3 bg-accent-500 text-primary-800 rounded-lg font-heading font-semibold text-sm hover:bg-accent-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-fill text-base"></i>
                </div>
                Checkout on WhatsApp
              </button>
            ) : (
              <div className="flex flex-col gap-3">
                <div>
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (nameError) setNameError('');
                    }}
                    className={`w-full px-3 py-2.5 text-sm font-label border rounded-md bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-400 transition-colors ${nameError ? 'border-red-400' : 'border-background-300'}`}
                  />
                  {nameError && (
                    <p className="text-xs text-red-500 mt-1 font-label">{nameError}</p>
                  )}
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={phone}
                    onChange={(e) => {
                      handlePhoneChange(e.target.value);
                      // Auto-detect: if pasted with +91, strip it
                    }}
                    onBlur={handlePhoneBlur}
                    className={`w-full px-3 py-2.5 text-sm font-label border rounded-md bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-400 transition-colors ${phoneTouched && phoneError ? 'border-red-400' : 'border-background-300'}`}
                  />
                  {phoneTouched && phoneError && (
                    <p className="text-xs text-red-500 mt-1 font-label">{phoneError}</p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Delivery City / Pincode"
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                      if (cityError) setCityError('');
                    }}
                    className={`w-full px-3 py-2.5 text-sm font-label border rounded-md bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-400 transition-colors ${cityError ? 'border-red-400' : 'border-background-300'}`}
                  />
                  {cityError && (
                    <p className="text-xs text-red-500 mt-1 font-label">{cityError}</p>
                  )}
                </div>
                <button
                  onClick={handleCheckout}
                  className="w-full py-3 bg-accent-500 text-primary-800 rounded-lg font-heading font-semibold text-sm hover:bg-accent-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-whatsapp-fill text-base"></i>
                  </div>
                  Send Order on WhatsApp
                </button>
              </div>
            )}

            <Link
              to="/cart"
              onClick={closeDrawer}
              className="block text-center mt-3 text-sm font-label font-medium text-foreground-500 hover:text-foreground-800 transition-colors"
            >
              View Full Cart
            </Link>
          </div>
        )}

        {/* Toast */}
        {toastMessage && (
          <div className="absolute bottom-20 left-5 right-5 bg-accent-500 text-primary-800 px-4 py-3 rounded-lg text-sm font-label font-medium text-center toast-enter animate-fade-in">
            {toastMessage}
          </div>
        )}
      </div>
    </>
  );
}